import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { API_BASE } from '../../config';
import { useLocale } from '../../contexts/LocaleContext';
import { AdminAccessDenied } from './AdminAccessDenied';
import {
  ADMIN_BADGE_INFO_CLASSNAME,
  ADMIN_BADGE_WARNING_CLASSNAME,
  ADMIN_PAGE_CLASSNAME,
  ADMIN_STATS_ROW_CLASSNAME,
  ADMIN_STAT_CARD_CLASSNAME,
  ADMIN_STAT_LABEL_CLASSNAME,
  ADMIN_STAT_VALUE_CLASSNAME,
  AdminEmptyState,
  AdminErrorAlert,
  AdminLoadingState,
  AdminTableWrap,
} from './admin-ui';

interface WordImport {
  id: string;
  userId: string;
  userEmail?: string;
  documentId: string | null;
  documentTitle?: string;
  importType: 'word' | 'pdf';
  createdAt: string;
}

export const WordImports: React.FC = () => {
  const { user, token, masterKey } = useAuth();
  const { t } = useLocale();
  const [wordImports, setWordImports] = useState<WordImport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchWordImports = async () => {
      if ((!token && !masterKey) || !user?.admin) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const url = masterKey
          ? `${API_BASE}/api/word-imports?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/word-imports`;
        const response = await fetch(url, {
          headers: {
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch word imports');
        }

        const data = await response.json();
        setWordImports(data.wordImports || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching word imports:', err);
        setError(t('failed_to_load_word_imports'));
      } finally {
        setLoading(false);
      }
    };

    fetchWordImports();
  }, [token, user, masterKey, t]);

  if (!user || !user.admin) {
    return <AdminAccessDenied />;
  }

  if (loading) {
    return <AdminLoadingState />;
  }

  const totalCount = wordImports.length;
  const wordCount = wordImports.filter(wi => wi.importType === 'word').length;
  const pdfCount = wordImports.filter(wi => wi.importType === 'pdf').length;

  return (
    <>
      <div className={ADMIN_PAGE_CLASSNAME}>
        {error && (
          <div className="mb-8">
            <AdminErrorAlert>{error}</AdminErrorAlert>
          </div>
        )}

        <div className={ADMIN_STATS_ROW_CLASSNAME}>
          <div className={ADMIN_STAT_CARD_CLASSNAME}>
            <div className={`${ADMIN_STAT_LABEL_CLASSNAME} mb-2`}>{t('total')}</div>
            <div className={ADMIN_STAT_VALUE_CLASSNAME}>{totalCount}</div>
          </div>
          <div className={ADMIN_STAT_CARD_CLASSNAME}>
            <div className={`${ADMIN_STAT_LABEL_CLASSNAME} mb-2`}>{t('word_doc')}</div>
            <div className={ADMIN_STAT_VALUE_CLASSNAME}>{wordCount}</div>
          </div>
          <div className={ADMIN_STAT_CARD_CLASSNAME}>
            <div className={`${ADMIN_STAT_LABEL_CLASSNAME} mb-2`}>{t('pdf')}</div>
            <div className={ADMIN_STAT_VALUE_CLASSNAME}>{pdfCount}</div>
          </div>
        </div>

        {wordImports.length === 0 ? (
          <AdminEmptyState>{t('no_document_imports_registered')}</AdminEmptyState>
        ) : (
          <AdminTableWrap>
            <table className="admin-data-table min-w-[1000px]">
              <thead>
                <tr>
                  <th>
                    {t('user')}
                  </th>
                  <th>
                    {t('type')}
                  </th>
                  <th>
                    {t('document')}
                  </th>
                  <th>
                    {t('document_title')}
                  </th>
                  <th>
                    {t('date')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {wordImports.map((wi) => (
                  <tr
                    key={wi.id}
                  >
                    <td>
                      {wi.userEmail ? (
                        <Link
                          to={`/admin/users/${wi.userId}`}
                          className="admin-table-link"
                        >
                          {wi.userEmail}
                        </Link>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      <span
                        className={`${wi.importType === 'word' ? ADMIN_BADGE_INFO_CLASSNAME : ADMIN_BADGE_WARNING_CLASSNAME} uppercase`}
                      >
                        {wi.importType === 'word' ? t('word_doc') : t('pdf')}
                      </span>
                    </td>
                    <td>
                      {wi.documentId ? (
                        <Link
                          to={`/documents/${wi.documentId}`}
                          className="admin-table-link"
                        >
                          {wi.documentId.substring(0, 8)}...
                        </Link>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="max-w-[300px]">
                      <div className="truncate" title={wi.documentTitle || '-'}>
                        {wi.documentTitle || '-'}
                      </div>
                    </td>
                    <td className="admin-table-cell-muted">
                      {new Date(wi.createdAt).toLocaleDateString('es-ES', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableWrap>
        )}
      </div>
    </>
  );
};
